<template>
  <div class="flex flex-wrap gap-4 w-full mb-2">
    <div class="flex-grow min-w-[100px] max-w-[250px]">
      <PinSearch :on-pin-grab="handlePinGrabAndReset" />
    </div>

    <div class="flex justify-between min-w-[200px]">
      <Icon
        icon="material-symbols:refresh"
        class="mr-10 cursor-pointer"
        :class="{ 'animate-spin': isSpinning }"
        :style="{ fontSize: '36px' }"
        @click="handleRefresh"
      />
      <PaginationDisplay
        :current-page="currentPage"
        :total-pages="totalPages"
        :on-prev-page="handlePrevPage"
        :on-next-page="handleNextPage"
      />
    </div>

    <div class="flex items-center flex-wrap gap-4">
      <el-checkbox v-model="tableStore.showButtons" class="px-4"
        >Show Buttons</el-checkbox
      >
      <div class="flex border min-w-[250px] rounded py-2 justify-around mr-4">
        Records shown per page:
        <div
          v-for="number in numbers"
          :key="number"
          :class="{
            'highlight-class': number === resultsOnPage && !allSelected,
          }"
          class="cursor-pointer"
          @click="updateCount(number)"
        >
          {{ number }}
        </div>
      </div>

      <div class="hidden">
        <el-tooltip
          content="This will load all reviewers to one page."
          placement="top"
        >
          <el-button
            class="my-auto whitespace-nowrap"
            type="primary"
            plain
            @click="loadAllReviewers"
          >
            Load All Reviewers
          </el-button>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { Icon } from "@iconify/vue";
import PaginationDisplay from "./PaginationDisplay.vue";
import { useReviewers, useUser, useTable, useDump } from "@/stores";
import { usePinStore } from "@/stores/pinStore";
import { useTableFilter } from "@/stores/tableFilterStore";
import PinSearch from "./PinSearch.vue";

const props = defineProps({
  onPinGrab: {
    type: Function,
    required: true,
  },
  onFetch: {
    type: Function,
    required: true,
  },
});

const handlePinGrabAndReset = async () => {
  // Reset page count when a pin is selected
  userStore.currentPage = 1;
  await props.onPinGrab();
};

const reviewersStore = useReviewers();
const userStore = useUser();
const tableStore = useTable();
const pinStore = usePinStore();
const tableFilter = useTableFilter();

// Watch for pin store errors and handle them
watch(
  () => pinStore.error,
  (error) => {
    if (error) {
      console.error("Pin search error:", error);
    }
  }
);

const allSelected = ref(false);
const resultsOnPage = ref(10);
const numbers = [10, 25, 50];

const isSpinning = computed(() => reviewersStore.loading);
const currentPage = computed(() => userStore.currentPage);
const totalPages = computed(() =>
  Math.ceil(reviewersStore.totalReviewerCount / resultsOnPage.value)
);

const dumpStore = useDump();

const handleRefresh = () => {
  dumpStore.handleTableRefresh(resultsOnPage.value);
  props.onPinGrab();
  resultsOnPage.value = 10;
  tableFilter.resetFilter();
  userStore.currentPage = 1;
};

const handlePrevPage = () => {
  userStore.currentPage -= 1;
  props.onFetch(resultsOnPage.value);
};

const handleNextPage = () => {
  userStore.currentPage += 1;
  props.onFetch(resultsOnPage.value);
};

const updateCount = (newCount) => {
  allSelected.value = false;
  resultsOnPage.value = newCount;
  userStore.currentPage = 1;
  props.onFetch(newCount);
};

const loadAllReviewers = () => {
  allSelected.value = true;
  reviewersStore.fetchReviewers(1, 3000, true);
  totalPages.value = Math.ceil(reviewersStore.totalReviewerCount / 3000);
  reviewersStore.loading = true;
};
</script>

<style>
.highlight-class {
  font-weight: bold;
  color: #00b7ff;
}

.dropdown-table .el-table__body-wrapper tbody tr:first-child {
  background-color: #007bff;
  color: white;
}
</style>
